/**
 * InboxPage
 * Displays conversations for signed-in users with a list → detail view.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listConversations,
  getConversation,
  sendReply,
  markRead,
  archiveConversation,
  isSignedIn,
  Conversation,
  ConversationDetail,
  Message,
} from '../services/messagingService';
import '../styles/colors.css';

const InboxPage: React.FC = () => {
  const navigate = useNavigate();

  // Redirect to home/login if not signed in
  useEffect(() => {
    if (!isSignedIn()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    const data = await listConversations();
    setConversations(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const openConversation = useCallback(async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    setReplyText('');
    const data = await getConversation(id);
    setDetail(data);
    setDetailLoading(false);
    // Mark as read
    await markRead(id);
    // Update unread count in list
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)),
    );
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages]);

  const handleSendReply = async () => {
    if (!selectedId || !replyText.trim()) return;
    setSending(true);
    const res = await sendReply(selectedId, replyText.trim());
    setSending(false);
    if (res.success) {
      setReplyText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      // Re-fetch to get updated messages
      const data = await getConversation(selectedId);
      setDetail(data);
      loadConversations(); // refresh list
    }
  };

  const handleArchive = async (id: number) => {
    const ok = await archiveConversation(id);
    if (ok) {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        setDetail(null);
      }
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d`;
    return d.toLocaleDateString();
  };

  // ---- Swipe-to-delete ----
  const [swipeState, setSwipeState] = useState<Record<number, number>>({});
  const swipeDragging = useRef<number | null>(null);
  const swipeTouchStartX = useRef(0);
  const swipeTouchStartY = useRef(0);
  const swipeIsHorizontal = useRef<boolean | null>(null);
  const SWIPE_THRESHOLD = 80;
  const SWIPE_MAX = 120;

  const handleSwipeDelete = async (id: number) => {
    // TODO: Replace confirm with soft-delete / trash in the future
    if (!window.confirm('Delete this conversation?')) return;
    await handleArchive(id);
  };

  const onRowTouchStart = (id: number, e: React.TouchEvent) => {
    swipeDragging.current = id;
    swipeTouchStartX.current = e.touches[0].clientX;
    swipeTouchStartY.current = e.touches[0].clientY;
    swipeIsHorizontal.current = null;
  };

  const onRowTouchMove = (id: number, e: React.TouchEvent) => {
    if (swipeDragging.current !== id) return;
    const dx = e.touches[0].clientX - swipeTouchStartX.current;
    const dy = e.touches[0].clientY - swipeTouchStartY.current;
    if (swipeIsHorizontal.current === null) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        swipeIsHorizontal.current = Math.abs(dx) > Math.abs(dy);
      }
    }
    if (!swipeIsHorizontal.current) return;
    e.preventDefault();
    if (dx > 0) return; // only left swipe
    let clamped = dx;
    if (Math.abs(clamped) > SWIPE_MAX) {
      const over = Math.abs(clamped) - SWIPE_MAX;
      clamped = -1 * (SWIPE_MAX + over * 0.2);
    }
    setSwipeState((prev) => ({ ...prev, [id]: clamped }));
  };

  const onRowTouchEnd = (id: number) => {
    if (swipeDragging.current !== id) return;
    swipeDragging.current = null;
    swipeIsHorizontal.current = null;
    const currentTx = swipeState[id] || 0;
    if (currentTx < -SWIPE_THRESHOLD) {
      handleSwipeDelete(id);
    }
    setSwipeState((prev) => ({ ...prev, [id]: 0 }));
  };

  const renderConversationRow = (c: Conversation) => {
    const tx = swipeState[c.id] || 0;
    const dragging = swipeDragging.current === c.id;
    const showDeleteBg = tx < -20;
    const deleteOpacity = Math.min(1, Math.abs(Math.min(0, tx)) / SWIPE_THRESHOLD);
    const notSwiping = Math.abs(tx) < 5;
    return (
      <div key={c.id} style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid var(--color-border)' }}>
        {showDeleteBg && (
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: Math.abs(tx),
            background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: deleteOpacity, color: '#fff', fontWeight: 600, fontSize: '14px', gap: 6,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Delete
          </div>
        )}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
            cursor: 'pointer',
            background: c.unread_count > 0 ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
            transform: `translateX(${tx}px)`,
            transition: dragging ? 'none' : 'transform 0.3s ease-out',
          }}
          onClick={() => { if (notSwiping) openConversation(c.id); }}
          onTouchStart={(e) => onRowTouchStart(c.id, e)}
          onTouchMove={(e) => onRowTouchMove(c.id, e)}
          onTouchEnd={() => onRowTouchEnd(c.id)}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: 'var(--color-accent)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: '600', fontSize: '16px', flexShrink: 0,
          }}>
            {(c.other_user.display_name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
              <span style={{ fontWeight: c.unread_count > 0 ? '600' : '500', fontSize: '15px' }}>
                {c.other_user.display_name || 'Unknown'}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                {c.last_message ? formatTime(c.last_message.created_at) : ''}
              </span>
            </div>
            {c.item && (
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '2px' }}>
                Re: {c.item.name}
              </div>
            )}
            <div style={{
              fontSize: '13px',
              color: c.unread_count > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              fontWeight: c.unread_count > 0 ? '500' : '400',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {c.last_message
                ? (c.last_message.is_mine ? 'You: ' : '') + c.last_message.body
                : 'No messages'}
            </div>
          </div>
          {c.unread_count > 0 && (
            <div style={{
              minWidth: 20, height: 20, borderRadius: '10px', background: 'var(--color-accent)',
              color: '#fff', fontSize: '11px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px',
            }}>
              {c.unread_count}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---- Render ----
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'var(--color-bg-secondary)',
        }}
      >
        <button
          onClick={() => (selectedId ? (setSelectedId(null), setDetail(null)) : navigate('/'))}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px',
          }}
          title="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          {selectedId && detail ? (detail.conversation.other_user.display_name || 'Conversation') : 'Inbox'}
        </h2>
      </div>

      {/* Content */}
      {!selectedId ? (
        /* ---- Conversation List ---- */
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Loading…
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <p style={{ fontSize: '15px', margin: 0 }}>No messages yet</p>
            </div>
          ) : (
            conversations.map(renderConversationRow)
          )}
        </div>
      ) : (
        /* ---- Conversation Detail ---- */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Loading…
            </div>
          ) : detail ? (
            <>
              {/* Item context bar */}
              {detail.conversation.item && (
                <div
                  style={{
                    padding: '8px 16px',
                    borderBottom: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)',
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  About: <strong>{detail.conversation.item.name}</strong>
                </div>
              )}
              {/* Messages */}
              <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                {detail.messages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      justifyContent: m.is_mine ? 'flex-end' : 'flex-start',
                      marginBottom: '10px',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '75%',
                        padding: '10px 14px',
                        borderRadius: m.is_mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: m.is_mine ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
                        color: m.is_mine ? '#fff' : 'var(--color-text-primary)',
                        fontSize: '14px',
                        lineHeight: '1.4',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {!m.is_mine && (
                        <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', opacity: 0.7 }}>
                          {m.sender_name}
                        </div>
                      )}
                      {m.body}
                      <div
                        style={{
                          fontSize: '10px',
                          opacity: 0.6,
                          marginTop: '4px',
                          textAlign: 'right',
                        }}
                      >
                        {formatTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {/* Reply input */}
              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid var(--color-border)',
                  background: 'var(--color-bg-secondary)',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'flex-end',
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={replyText}
                  onChange={(e) => {
                    setReplyText(e.target.value);
                    // Auto-resize: reset to 1 row then grow to content, max 10 lines
                    const ta = e.target;
                    ta.style.height = 'auto';
                    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                  placeholder="Type a message…"
                  rows={1}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '20px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-primary)',
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'inherit',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                />
                <button
                  onClick={handleSendReply}
                  disabled={sending || !replyText.trim()}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: 'none',
                    background: replyText.trim() ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                    color: replyText.trim() ? '#fff' : 'var(--color-text-muted)',
                    cursor: replyText.trim() ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Failed to load conversation
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InboxPage;
